// src/admin/AddStaffMemberDialog.tsx
/**
 * "Add ButtonUp staff" (spec §6.2 / §7): picks an Active Owner or Admin of org_button_up who is
 * not already a member here and creates an immediate membership, no email. The View is pure (node
 * vitest); the container loads the staff list (with Retry) and submits.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/ui/modal';
import { NativeSelect } from '@/components/ui/native-select';
import { Spinner } from '@/components/ui/spinner';
import { adminApi, type AdminUser, type StaffMemberRole } from '../services/adminApi';
import {
  STAFF_MEMBER_ROLES,
  STAFF_ORGANIZATION_ID,
  mapAddStaffFailure,
  selectableStaff,
} from './orgProvisioning';
import { useDialogEscape } from './useDialogEscape';

const LABEL_CLASS = 'mb-1 block text-sm font-semibold';
const ALERT_CLASS = 'rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive';

export interface AddStaffMemberDialogViewProps {
  staff: AdminUser[];
  staffLoading: boolean;
  staffError: string | null;
  accountId: string;
  role: StaffMemberRole;
  error: string | null;
  submitting: boolean;
  onAccountChange: (accountId: string) => void;
  onRoleChange: (role: StaffMemberRole) => void;
  onRetryStaff: () => void;
  onSubmit: () => void;
  onClose: () => void;
}

export function AddStaffMemberDialogView({
  staff,
  staffLoading,
  staffError,
  accountId,
  role,
  error,
  submitting,
  onAccountChange,
  onRoleChange,
  onRetryStaff,
  onSubmit,
  onClose,
}: AddStaffMemberDialogViewProps) {
  const canSubmit = !submitting && !staffLoading && !staffError && accountId !== '';
  return (
    <Modal open onOverlayClick={() => { if (!submitting) onClose(); }}>
      <Modal.Content maxWidth="lg" aria-labelledby="add-staff-title">
        <Modal.Header className="px-6 py-4">
          <h2 id="add-staff-title" className="text-lg font-medium text-foreground">Add ButtonUp staff</h2>
        </Modal.Header>
        <Modal.Body className="px-6 py-4">
          <div className="flex flex-col gap-4">
            <p className="text-sm text-muted-foreground">
              Gives an existing ButtonUp staff account immediate membership. No email is sent, and
              staff never count as the customer&apos;s owner. Staff who are already members are not listed.
            </p>

            <div>
              {staffLoading ? (
                <Spinner size={16} label="Loading staff…" />
              ) : staffError ? (
                <div role="alert" className={`${ALERT_CLASS} flex items-center justify-between gap-2`}>
                  <span>{staffError}</span>
                  <Button type="button" variant="ghost" size="sm" onClick={onRetryStaff}>Retry</Button>
                </div>
              ) : (
                <>
                  <label htmlFor="add-staff-account" className={LABEL_CLASS}>Staff member</label>
                  <NativeSelect
                    id="add-staff-account"
                    value={accountId}
                    onChange={(e) => onAccountChange(e.target.value)}
                    disabled={staff.length === 0}
                    autoFocus
                  >
                    <option value="">{staff.length === 0 ? 'No staff accounts left to add' : 'Choose a staff member…'}</option>
                    {staff.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name ? `${u.name} (${u.email})` : u.email}
                      </option>
                    ))}
                  </NativeSelect>
                </>
              )}
            </div>

            <div>
              <label htmlFor="add-staff-role" className={LABEL_CLASS}>Role</label>
              <NativeSelect
                id="add-staff-role"
                value={role}
                onChange={(e) => onRoleChange(e.target.value as StaffMemberRole)}
              >
                {STAFF_MEMBER_ROLES.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </NativeSelect>
            </div>

            {error && <div role="alert" className={ALERT_CLASS}>{error}</div>}
          </div>
        </Modal.Body>
        <Modal.Footer className="flex justify-end gap-2 px-6 py-4">
          <Button variant="secondary" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={onSubmit} disabled={!canSubmit}>
            {submitting ? 'Adding…' : 'Add as member'}
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
}

export function AddStaffMemberDialog({
  orgId,
  existingMemberAccountIds,
  onAdded,
  onClose,
}: {
  orgId: string;
  /** Account ids already holding a membership here (any status); they are left out of the picker. */
  existingMemberAccountIds: string[];
  onAdded: () => void;
  onClose: () => void;
}) {
  const [staff, setStaff] = useState<AdminUser[]>([]);
  const [staffLoading, setStaffLoading] = useState(true);
  const [staffError, setStaffError] = useState<string | null>(null);
  const [accountId, setAccountId] = useState('');
  const [role, setRole] = useState<StaffMemberRole>('Admin');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Read through a ref so a parent re-render (a fresh array each time) never refetches the list.
  const excludeRef = useRef(existingMemberAccountIds);
  excludeRef.current = existingMemberAccountIds;

  useDialogEscape(onClose, !submitting);

  const loadStaff = useCallback(() => {
    let cancelled = false;
    setStaffLoading(true);
    setStaffError(null);
    // The users endpoint caps `take` at 100; the ButtonUp org is well inside that.
    adminApi.getOrganizationUsers(STAFF_ORGANIZATION_ID, 'Active', 0, 100)
      .then((data) => {
        if (!cancelled) setStaff(selectableStaff(data.users, excludeRef.current));
      })
      .catch((err: unknown) => {
        console.error('Failed to load ButtonUp staff:', err);
        if (!cancelled) setStaffError('Failed to load the ButtonUp staff list');
      })
      .finally(() => {
        if (!cancelled) setStaffLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => loadStaff(), [loadStaff]);

  const handleSubmit = async () => {
    if (!accountId) return;
    setSubmitting(true);
    setError(null);
    try {
      await adminApi.addStaffMember(orgId, { accountId, role });
      onAdded();
    } catch (err: unknown) {
      setError(mapAddStaffFailure(err));
      setSubmitting(false);
    }
  };

  return (
    <AddStaffMemberDialogView
      staff={staff}
      staffLoading={staffLoading}
      staffError={staffError}
      accountId={accountId}
      role={role}
      error={error}
      submitting={submitting}
      onAccountChange={setAccountId}
      onRoleChange={setRole}
      onRetryStaff={() => { loadStaff(); }}
      onSubmit={() => void handleSubmit()}
      onClose={onClose}
    />
  );
}
